import React from "react"
import { Footer } from "./Footer"
import { FormContainer, Worksheet, LabeledInput } from "uikit"
import { Dropdown } from "semantic-ui-react"
import { useStore } from "store"

const GroupLifePlanForm = ({
  data: {
    fields: {
      income_id,
      death_benefit,
      premium_amount,
      death_benefit_spouse,
      premium_amount_spouse
    },
    isModified,
    onSubmit,
    isPending,
    hasError,
    error
  },
  submitLabel = "Done",
  submitHeader = "",
  isNew,
  onDelete,
  spouseInfo,
  clientInfo,
  incomeSourceOptions,
  getEmployeeName,
  getSpouseName
}) => {
  return (
    <Worksheet error={error}>
      <Worksheet.Body>
        <Worksheet.Row
          label="Which income source is associated with this plan?"
          paddedField
        >
          <Worksheet.Field error={income_id.error}>
            <Dropdown
              {...income_id}
              options={incomeSourceOptions}
              placeholder="Employment"
              selection
            />
          </Worksheet.Field>
        </Worksheet.Row>
        <Worksheet.Row
          label={
            "What is the insurance benefit provided to " +
            getEmployeeName(income_id.value) +
            ", as the employee?"
          }
          paddedField
          description=""
        >
          <Worksheet.Field error={death_benefit.error}>
            <LabeledInput
              {...death_benefit}
              label=""
              iconText="$"
              iconPosition="left"
              placeholder="0"
            />
          </Worksheet.Field>
        </Worksheet.Row>
        <Worksheet.Row
          label="How much does it cost per pay period?"
          paddedField
          description=""
        >
          <Worksheet.Field error={premium_amount.error}>
            <LabeledInput
              {...premium_amount}
              label=""
              iconText="$"
              iconPosition="left"
              placeholder="0"
            />
          </Worksheet.Field>
        </Worksheet.Row>
        {spouseInfo ? (
          <Worksheet.Row
            label={
              "What is the insurance benefit provided to " +
              getSpouseName(income_id.value) +
              "?"
            }
            paddedField
            description=""
          >
            <Worksheet.Field error={death_benefit_spouse.error}>
              <LabeledInput
                {...death_benefit_spouse}
                label=""
                iconText="$"
                iconPosition="left"
                placeholder="0"
              />
            </Worksheet.Field>
          </Worksheet.Row>
        ) : null}
        {spouseInfo ? (
          <Worksheet.Row
            label={
              "How much does it cost per pay period for " +
              getSpouseName(income_id.value) +
              "?"
            }
            paddedField
            description=""
          >
            <Worksheet.Field error={premium_amount_spouse.error}>
              <LabeledInput
                {...premium_amount_spouse}
                label=""
                iconText="$"
                iconPosition="left"
                placeholder="0"
              />
            </Worksheet.Field>
          </Worksheet.Row>
        ) : null}
      </Worksheet.Body>
      <Footer
        isNew={isNew}
        submitLabel={submitLabel}
        onSubmit={onSubmit}
        onDelete={onDelete}
        isModified={isModified}
        deleteLabel="Delete Benefit"
        error={error}
      />
    </Worksheet>
  )
}

const GroupLifePlanFormContainer = FormContainer(
  {
    income_id: {
      type: "string"
    },
    death_benefit: {
      type: "unit"
    },
    premium_amount: {
      type: "unit",
      defaultValue: 0
    },
    death_benefit_spouse: {
      type: "unit",
      required: false,
      defaultValue: 0
    },
    premium_amount_spouse: {
      type: "unit",
      defaultValue: 0,
      required: false
    }
  },
  (fields, ...form) => {}
)(GroupLifePlanForm)

const GroupLifePlan = ({ benefit, household, type, ...formProps }) => {
  const [incomes] = useStore("householdIncomes", household.id)
  const [people] = useStore("householdPeople", household.id)
  const [employers, { addBenefit, deleteBenefit, updateBenefit }] = useStore(
    "householdEmployers",
    household.id
  )

  const isNew = benefit === null
  const spouseInfo = Object.values(people).find(
    person => person.role === "spouse"
  )
  const clientInfo = Object.values(people).find(
    person => person.role === "client"
  )
  let values = {
    income_id:
      Object.values(incomes).length > 0 ? Object.values(incomes)[0].id : null
  }
  if (!isNew) {
    values = Object.assign({}, benefit)
  }
  const hasSpouse = Object.values(people).some(({ role }) => role === "spouse")

  const incomeSourceOptions = Object.values(incomes).map(income => {
    const employer = Object.values(employers).find(
      ({ id }) => id === income.employer_id
    )
    const incomeName = employer ? employer.name : income.description
    const text =
      hasSpouse && income.person_id
        ? `${incomeName} (${people[income.person_id].first_name})`
        : incomeName
    return {
      text,
      value: income.id
    }
  })

  const getEmployeeName = incomeId => {
    if (incomeId) {
      const income = Object.values(incomes).find(
        income => income.id === incomeId
      )
      const owner = Object.values(people).find(
        person => person.id === income.person_id
      )
      return owner ? owner.first_name : ""
    }
    return ""
  }

  const getSpouseName = incomeId => {
    if (incomeId) {
      const income = Object.values(incomes).find(
        income => income.id === incomeId
      )
      const owner = Object.values(people).find(
        person => person.id === income.person_id
      )
      if (owner) {
        if (owner.role === "client") {
          const spouseInfo = Object.values(people).find(
            person => person.role === "spouse"
          )
          return spouseInfo ? spouseInfo.first_name : ""
        } else {
          const spouseInfo = Object.values(people).find(
            person => person.role === "client"
          )
          return spouseInfo ? spouseInfo.first_name : ""
        }
      }
    }
    return ""
  }

  const submit = isNew
    ? async values =>
        await addBenefit({
          household_id: household.id,
          type,
          ...values
        })
    : async values =>
        await updateBenefit({
          id: benefit.id,
          type,
          ...values
        })

  const onDelete = () =>
    deleteBenefit({
      id: benefit.id,
      household_id: household.id
    })

  return (
    <GroupLifePlanFormContainer
      {...{
        isNew,
        submit,
        onDelete,
        values,
        spouseInfo,
        clientInfo,
        incomeSourceOptions,
        getEmployeeName,
        getSpouseName,
        ...formProps
      }}
    />
  )
}
export default GroupLifePlan
